import { useEffect, useMemo, useState } from 'react';
import './QuoteCopyModal.css';

import { Card, Button, Form, Row, Col } from 'react-bootstrap';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import { FlexGrid, FlexGridColumn, FlexGridCellTemplate } from '@grapecity/wijmo.react.grid';
import * as wjcGrid from '@grapecity/wijmo.grid';
import { CellType } from '@grapecity/wijmo.grid';

import { useMaster } from 'context/MasterContext';
import { useRestApi } from 'context/RestApiContext';

// 集計(グループ)行のセル結合
class CustomMergeManager extends wjcGrid.MergeManager {
  getMergedRange(panel, r, c, clip = true) {
    const rng = new wjcGrid.CellRange(r, c);
    const dataItem = panel?.grid?.rows?.[r]?.dataItem;

    if (panel.cellType === CellType.Cell && dataItem?.Group) {
      if (c >= 1 && c < 4) {
        rng.col = 1;
        rng.col2 = 3;
        return rng;
      }
      if (c > 4 && c < 7) {
        rng.col = 5;
        rng.col2 = 6;
        return rng;
      }
    }

    return rng;
  }
}

function QuoteDetailModal({
  show,
  setShow,
  quote,
  onCopy,
  onCloseParent,
  useLatestPrice,
  setUseLatestPrice,
}) {
  const restApi = useRestApi();
  const master = useMaster();

  const [viewQuoteDetails, setViewQuoteDetails] = useState([]);
  // 選択された明細行のキー(quote.details内のindex)を保持
  const [checkedDetailKeys, setCheckedDetailKeys] = useState(new Set());

  // 工事種別(QUOTE_KNO)ごとの明細キー一覧
  const knoToKeys = useMemo(() => {
    const map = {};
    (quote?.details || []).forEach((d, idx) => {
      const kno = d.QUOTE_KNO;
      if (!map[kno]) map[kno] = [];
      map[kno].push(idx);
    });
    return map;
  }, [quote]);

  // 全明細キー
  const allDetailKeys = useMemo(
    () => (quote?.details || []).map((_, idx) => idx),
    [quote],
  );

  const allChecked =
    allDetailKeys.length > 0 && allDetailKeys.every((key) => checkedDetailKeys.has(key));
  const someChecked = allDetailKeys.some((key) => checkedDetailKeys.has(key));

  // 工事種別行のチェック状態を判定
  const getGroupCheckState = (kno) => {
    const keys = knoToKeys[kno] || [];
    if (keys.length === 0) return { checked: false, indeterminate: false };
    const checkedCount = keys.filter((key) => checkedDetailKeys.has(key)).length;
    return {
      checked: checkedCount === keys.length,
      indeterminate: checkedCount > 0 && checkedCount < keys.length,
    };
  };

  const planerWithEmpty = useMemo(() => [...master.planer, { id: 0, name: '' }], [master.planer]);

  const initializedGrid = (flex) => {
    flex.mergeManager = new CustomMergeManager();
  };

  useEffect(() => {
    if (!quote?.kbns || !quote?.details) {
      setViewQuoteDetails([]);
      return;
    }

    const rows = [];
    let currentKno = null;

    quote.details.forEach((detail, idx) => {
      if (currentKno !== detail.QUOTE_KNO) {
        currentKno = detail.QUOTE_KNO;

        const kbn = quote.kbns.find((x) => x.QUOTE_KNO === detail.QUOTE_KNO);
        rows.push({
          Group: true,
          QUOTE_KNO: detail.QUOTE_KNO,
          CONTENT: kbn
            ? `${String.fromCharCode(64 + detail.QUOTE_KNO)}. ${kbn.KOUJI_DISPLAY_NAME || kbn.KOUJI_SYU}`
            : '',
          KINGAKU: kbn ? kbn.TOTAL : 0,
        });
      }

      // _key で quote.details 内のindexを保持(明細単位の選択に利用)
      rows.push({ ...detail, _key: idx });
    });

    setViewQuoteDetails(rows);
    setCheckedDetailKeys(new Set());
  }, [quote]);

  // 明細行単位のチェック
  const handleDetailCheckChange = (key, checked) => {
    setCheckedDetailKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  // 工事種別行のチェック(配下の明細を一括選択/解除)
  const handleGroupCheckChange = (kno, checked) => {
    const keys = knoToKeys[kno] || [];
    setCheckedDetailKeys((prev) => {
      const next = new Set(prev);
      keys.forEach((key) => {
        if (checked) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  };

  const handleToggleAll = (checked) => {
    setCheckedDetailKeys(checked ? new Set(allDetailKeys) : new Set());
  };

  // 個別コピー
  const handleAdd = () => {
    if (checkedDetailKeys.size === 0) {
      alert('明細を選択してください');
      return;
    }

    // 選択された明細のみ抽出
    const selectedDetails = quote.details.filter((_, idx) => checkedDetailKeys.has(idx));
    // 選択された明細を含む工事種別のみ抽出
    const selectedKnos = new Set(selectedDetails.map((d) => d.QUOTE_KNO));
    const selectedKbns = quote.kbns.filter((k) => selectedKnos.has(k.QUOTE_KNO));

    const doCopy = (details) => {
      onCopy?.(quote.QUOTE_NO, { kbns: selectedKbns, details });
      setShow(false);
      onCloseParent?.();
    };

    // 最新単価を使わないならそのまま
    if (!useLatestPrice) {
      doCopy(selectedDetails);
      return;
    }

    // BASE_NO のリストを取得
    const baseNos = [
      ...new Set(
        selectedDetails.filter((d) => d.BASE_NO && d.BASE_NO > 0).map((d) => d.BASE_NO),
      ),
    ];

    // BASE_NO が無いならそのまま
    if (baseNos.length === 0) {
      doCopy(selectedDetails);
      return;
    }

    // 最新単価を取得して明細へ反映
    restApi.post('/api/quote/latest-prices', { BASE_NOS: baseNos }, (priceMap) => {
      const updatedDetails = selectedDetails.map((detail) => {
        const latest = detail.BASE_NO ? priceMap?.[String(detail.BASE_NO)] : null;
        if (!latest) return detail;

        const su = Number(detail.SU ?? 0);
        const tankA = Number(latest.TANKA) || Number(detail.TANKA) || 0;
        const genkaTankA = Number(latest.GENKA_TANKA) || Number(detail.GENKA_TANKA) || 0;

        if (detail.KOUJI_TYPE === 3 && Number(detail.KOUJIHI_PER ?? 0) <= 100) {
          return {
            ...detail,
            TANKA: tankA,
            GENKA_RATE: Number(latest.GENKA_RATE ?? detail.GENKA_RATE ?? 0),
            GENKA_TANKA: genkaTankA,
          };
        }

        const kingaku = su * tankA;
        const genkaKingaku = su * genkaTankA;

        return {
          ...detail,
          TANKA: tankA,
          GENKA_RATE: Number(latest.GENKA_RATE ?? detail.GENKA_RATE ?? 0),
          GENKA_TANKA: genkaTankA,
          KINGAKU: kingaku,
          GENKA_KINGAKU: genkaKingaku,
        };
      });

      doCopy(updatedDetails);
    });
  };

  // グループ行だけ背景色を変える
  const formatItem = (grid, e) => {
    if (e.panel.cellType !== CellType.Cell) return;

    const dataItem = grid.rows[e.row].dataItem;
    if (dataItem?.Group) {
      e.cell.style.background = 'rgb(255, 160, 122)';
      e.cell.style.color = 'rgb(255, 255, 255)';
      grid.rows[e.row].height = 35;
      return;
    }

    e.cell.style.background = '';
    e.cell.style.color = '';
  };

  return (
    <Dialog
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          width: '1600px',
          maxWidth: '95vw',
        },
      }}
      open={show}
      disableEnforceFocus
    >
      <DialogTitle className="p-3">見積詳細 - {quote?.KOUJI_MEI}</DialogTitle>

      <Card className="m-0">
        <Card.Body>
          <Form>
            <Row>
              <Col md="4" className="pr-1">
                <Form.Group>
                  <label className="item-header-label">工事名</label>
                  <Form.Control value={quote?.KOUJI_MEI ?? ''} disabled />
                </Form.Group>
              </Col>

              <Col md="4" className="pr-1">
                <Form.Group>
                  <label className="item-header-label">住所</label>
                  <Form.Control value={quote?.ADDRESS ?? ''} disabled />
                </Form.Group>
              </Col>

              <Col md="2" className="pr-1">
                <Form.Group>
                  <label className="item-header-label">顧客名</label>
                  <Form.Control value={quote?.CLIENT_NAME ?? ''} disabled />
                </Form.Group>
              </Col>

              <Col md="2" className="pr-1">
                <Form.Group>
                  <label className="item-header-label">営業担当</label>
                  <Form.Control
                    value={planerWithEmpty.find((p) => p.id === quote?.SALES_TAN_CD)?.name || ''}
                    disabled
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md="2" className="pr-1">
                <Form.Group>
                  <label className="item-header-label">金額</label>
                  <Form.Control
                    value={quote?.MITSU != null ? `¥${Number(quote.MITSU).toLocaleString()}` : ''}
                    disabled
                  />
                </Form.Group>
              </Col>

              <Col md="2" className="pr-1">
                <Form.Group>
                  <label className="item-header-label">見積日</label>
                  <Form.Control value={quote?.MITSU_DATE ?? ''} disabled />
                </Form.Group>
              </Col>
            </Row>
          </Form>

          <DialogContent>
            <div style={{ flex: 1, overflow: 'auto', height: 'calc(90vh - 320px)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => {
                    if (el) el.indeterminate = !allChecked && someChecked;
                  }}
                  onChange={(e) => handleToggleAll(e.target.checked)}
                />
                <span>全選択</span>
              </div>

              <FlexGrid
                itemsSource={viewQuoteDetails}
                selectionMode="None"
                headersVisibility="Column"
                allowSorting={false}
                initialized={initializedGrid}
                formatItem={formatItem}
                isReadOnly
              >
                <FlexGridColumn binding="" header="" width={40}>
                  <FlexGridCellTemplate
                    cellType="Cell"
                    template={(cell) => {
                      if (cell.item?.Group) {
                        const groupState = getGroupCheckState(cell.item.QUOTE_KNO);
                        return (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={groupState.checked}
                              ref={(el) => {
                                if (el) el.indeterminate = groupState.indeterminate;
                              }}
                              onChange={(e) =>
                                handleGroupCheckChange(cell.item.QUOTE_KNO, e.target.checked)
                              }
                              style={{ width: 18, height: 18, cursor: 'pointer' }}
                            />
                          </div>
                        );
                      }

                      return (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checkedDetailKeys.has(cell.item._key)}
                            onChange={(e) =>
                              handleDetailCheckChange(cell.item._key, e.target.checked)
                            }
                            style={{ width: 16, height: 16, cursor: 'pointer' }}
                          />
                        </div>
                      );
                    }}
                  />
                </FlexGridColumn>

                <FlexGridColumn binding="CONTENT" header="名称" width={250} />
                <FlexGridColumn binding="SIYOU" header="仕様" width={200} />
                <FlexGridColumn binding="SU" header="数量" format="n2" width={80} dataType="Number" />
                <FlexGridColumn binding="TANI" header="単位" width={60} />
                <FlexGridColumn binding="TANKA" header="単価" format="c0" width={100} dataType="Number" />
                <FlexGridColumn binding="KINGAKU" header="金額" format="c0" width={120} dataType="Number" />
                <FlexGridColumn binding="BIKO" header="備考" width={300} />
                <FlexGridColumn binding="BIKO_SYANAI" header="社内備考" width="1*" />
              </FlexGrid>
            </div>
          </DialogContent>
        </Card.Body>
      </Card>

      <DialogActions className="p-3" style={{ justifyContent: 'space-between' }}>
        <Button variant="secondary" className="footer-button" onClick={() => setShow(false)}>
          キャンセル
        </Button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label>
            <input
              className="mr-1"
              style={{ transform: 'scale(1.5)' }}
              type="checkbox"
              checked={useLatestPrice}
              onChange={(e) => setUseLatestPrice(e.target.checked)}
            />
            最新の単価でコピー
          </label>

          <Button variant="primary" onClick={handleAdd} className="btn-fill footer-button">
            追加
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
}

export default QuoteDetailModal;